/**
 * EffectivePermissionsDiff (Sprint 5 S5-B-components)
 *
 * Pure presentational BEFORE / AFTER permission-set diff for the FI Staff
 * "Edit Roles" flow. The role-edit modal in StaffDetail wires
 * `useEffectivePermissionsPreview` and passes the resulting `current` /
 * `proposed` / `diff` payload as props (Option A from the lane brief).
 * Composable: receives flat arrays for permissions and `RoleRef`-shaped
 * arrays for roles, plus an optional pre-computed permission diff and an
 * optional `serverError` for the LAST_ADMIN_INVARIANT alert.
 *
 * Two columns rendered via `@aaimplatform/ui` Card primitives. Permission
 * additions render with a leading "+" in the success/growth color.
 * Removals render with a leading "-" in the danger/error color and
 * strikethrough. Unchanged entries render in the secondary text color.
 * Roles render with the same diff vocabulary.
 *
 * Sprint 1 last-admin invariant: when `serverError.code` is
 * `LAST_ADMIN_INVARIANT` the component renders an inline Alert above the
 * diff with the canonical operator copy.
 */

import type { RoleRef } from '../../hooks/useStaffLifecycle';
import type { AbacPredicateRef } from '../../hooks/useEffectivePermissionsPreview';
import { Alert, Card } from '@aaimplatform/ui';

export interface EffectivePermissionsDiffServerError {
  code: string;
  message: string;
}

export interface EffectivePermissionsDiffProps {
  /** Permissions held by the staff member before the proposed change. */
  beforePermissions: string[];
  /** Permissions the staff member would hold after the proposed change. */
  afterPermissions: string[];
  /** Roles bound to the staff member before the proposed change. */
  beforeRoles: RoleRef[];
  /** Roles the staff member would hold after the proposed change. */
  afterRoles: RoleRef[];
  /**
   * Optional: ABAC predicates currently in effect. Rendered alongside
   * permissions for completeness; the Sprint 4 evaluator consumes both.
   */
  beforeAbacPredicates?: AbacPredicateRef[];
  /** Optional: ABAC predicates that would be in effect after the change. */
  afterAbacPredicates?: AbacPredicateRef[];
  /**
   * Optional server-side error returned by `updateStaffRoles`. When the
   * `code` is `LAST_ADMIN_INVARIANT`, the component renders an inline
   * Alert above the diff. Other codes are ignored here -- the parent
   * surfaces them through its own error path.
   */
  serverError?: EffectivePermissionsDiffServerError;
  /** Optional className passthrough for layout overrides. */
  className?: string;
}

interface DiffEntry {
  value: string;
  state: 'added' | 'removed' | 'unchanged';
}

function diffStringSet(before: string[], after: string[]): DiffEntry[] {
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  const seen = new Set<string>();
  const entries: DiffEntry[] = [];

  for (const value of after) {
    if (seen.has(value)) continue;
    seen.add(value);
    entries.push({
      value,
      state: beforeSet.has(value) ? 'unchanged' : 'added',
    });
  }
  for (const value of before) {
    if (seen.has(value)) continue;
    seen.add(value);
    if (!afterSet.has(value)) {
      entries.push({ value, state: 'removed' });
    }
  }
  entries.sort((a, b) => a.value.localeCompare(b.value));
  return entries;
}

interface RoleDiffEntry {
  id: string;
  slug: string;
  name: string;
  state: 'added' | 'removed' | 'unchanged';
}

function diffRoleSet(before: RoleRef[], after: RoleRef[]): RoleDiffEntry[] {
  const beforeIds = new Set(before.map((r) => r.id));
  const afterIds = new Set(after.map((r) => r.id));
  const byId = new Map<string, RoleRef>();
  for (const r of before) byId.set(r.id, r);
  for (const r of after) byId.set(r.id, r);

  const entries: RoleDiffEntry[] = [];
  for (const [id, role] of byId.entries()) {
    let state: RoleDiffEntry['state'];
    if (afterIds.has(id) && !beforeIds.has(id)) state = 'added';
    else if (beforeIds.has(id) && !afterIds.has(id)) state = 'removed';
    else state = 'unchanged';
    entries.push({ id, slug: role.slug, name: role.name, state });
  }
  entries.sort((a, b) => a.name.localeCompare(b.name));
  return entries;
}

interface PredicateDiffEntry {
  policyId: string;
  name: string;
  state: 'added' | 'removed' | 'unchanged';
}

function diffPredicateSet(
  before: AbacPredicateRef[],
  after: AbacPredicateRef[],
): PredicateDiffEntry[] {
  const beforeIds = new Set(before.map((p) => p.policyId));
  const afterIds = new Set(after.map((p) => p.policyId));
  const byId = new Map<string, AbacPredicateRef>();
  for (const p of before) byId.set(p.policyId, p);
  for (const p of after) byId.set(p.policyId, p);

  const entries: PredicateDiffEntry[] = [];
  for (const [policyId, ref] of byId.entries()) {
    let state: PredicateDiffEntry['state'];
    if (afterIds.has(policyId) && !beforeIds.has(policyId)) state = 'added';
    else if (beforeIds.has(policyId) && !afterIds.has(policyId)) state = 'removed';
    else state = 'unchanged';
    entries.push({ policyId, name: ref.name, state });
  }
  entries.sort((a, b) => a.name.localeCompare(b.name));
  return entries;
}

const STATE_STYLES: Record<DiffEntry['state'], { sigil: string; cls: string }> = {
  added: { sigil: '+', cls: 'text-growth' },
  removed: { sigil: '-', cls: 'text-error line-through' },
  unchanged: { sigil: ' ', cls: 'text-secondary' },
};

function DiffList({
  entries,
  emptyLabel,
}: {
  entries: DiffEntry[];
  emptyLabel: string;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-xs font-sans text-tertiary italic">{emptyLabel}</p>
    );
  }
  return (
    <ul className="space-y-1 font-mono text-sm">
      {entries.map((entry) => {
        const style = STATE_STYLES[entry.state];
        return (
          <li
            key={`${entry.state}-${entry.value}`}
            className={`flex items-baseline gap-2 ${style.cls}`}
          >
            <span aria-hidden="true" className="w-3 text-center select-none">
              {style.sigil}
            </span>
            <span className="break-all">{entry.value}</span>
            <span className="sr-only">
              {entry.state === 'added'
                ? ' added'
                : entry.state === 'removed'
                  ? ' removed'
                  : ' unchanged'}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function RoleDiffList({
  entries,
  emptyLabel,
}: {
  entries: RoleDiffEntry[];
  emptyLabel: string;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-xs font-sans text-tertiary italic">{emptyLabel}</p>
    );
  }
  return (
    <ul className="space-y-1 text-sm">
      {entries.map((entry) => {
        const style = STATE_STYLES[entry.state];
        return (
          <li
            key={entry.id}
            className={`flex items-baseline gap-2 ${style.cls}`}
          >
            <span aria-hidden="true" className="w-3 text-center select-none font-mono">
              {style.sigil}
            </span>
            <span className="font-sans font-semibold">{entry.name}</span>
            <span className="font-mono text-xs text-tertiary">
              {entry.slug}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function PredicateDiffList({
  entries,
  emptyLabel,
}: {
  entries: PredicateDiffEntry[];
  emptyLabel: string;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-xs font-sans text-tertiary italic">{emptyLabel}</p>
    );
  }
  return (
    <ul className="space-y-1 text-sm">
      {entries.map((entry) => {
        const style = STATE_STYLES[entry.state];
        return (
          <li
            key={entry.policyId}
            className={`flex items-baseline gap-2 ${style.cls}`}
          >
            <span aria-hidden="true" className="w-3 text-center select-none font-mono">
              {style.sigil}
            </span>
            <span className="font-sans">{entry.name}</span>
          </li>
        );
      })}
    </ul>
  );
}

const LAST_ADMIN_COPY =
  'This change would leave the tenant without an active fi_admin. Add another admin first or assign the fi_admin role to a remaining staff member.';

/**
 * Effective Permissions Diff -- pure presentational BEFORE / AFTER view
 * for the role-edit flow. Keep the parent in charge of fetching the
 * preview (`useEffectivePermissionsPreview`) and persisting the change
 * (`useUpdateStaffRoles`).
 */
export function EffectivePermissionsDiff({
  beforePermissions,
  afterPermissions,
  beforeRoles,
  afterRoles,
  beforeAbacPredicates,
  afterAbacPredicates,
  serverError,
  className,
}: EffectivePermissionsDiffProps) {
  const showLastAdminAlert = serverError?.code === 'LAST_ADMIN_INVARIANT';

  const permissionEntries = diffStringSet(beforePermissions, afterPermissions);
  const roleEntries = diffRoleSet(beforeRoles, afterRoles);

  const showPredicates =
    Array.isArray(beforeAbacPredicates) || Array.isArray(afterAbacPredicates);
  const predicateEntries = showPredicates
    ? diffPredicateSet(
        beforeAbacPredicates ?? [],
        afterAbacPredicates ?? [],
      )
    : [];

  return (
    <div className={`space-y-6 ${className ?? ''}`}>
      {showLastAdminAlert && (
        <Alert type="error" title="Last admin invariant">
          {LAST_ADMIN_COPY}
        </Alert>
      )}

      <section aria-label="Role diff">
        <h3 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
          Roles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <h4 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
              Before
            </h4>
            <RoleDiffList
              entries={roleEntries.filter((e) => e.state !== 'added')}
              emptyLabel="No roles bound."
            />
          </Card>
          <Card>
            <h4 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
              After
            </h4>
            <RoleDiffList
              entries={roleEntries.filter((e) => e.state !== 'removed')}
              emptyLabel="No roles bound."
            />
          </Card>
        </div>
      </section>

      <section aria-label="Permission diff">
        <h3 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
          Permissions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <h4 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
              Before
            </h4>
            <DiffList
              entries={permissionEntries.filter((e) => e.state !== 'added')}
              emptyLabel="No permissions held."
            />
          </Card>
          <Card>
            <h4 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
              After
            </h4>
            <DiffList
              entries={permissionEntries.filter((e) => e.state !== 'removed')}
              emptyLabel="No permissions held."
            />
          </Card>
        </div>
      </section>

      {showPredicates && (
        <section aria-label="ABAC predicate diff">
          <h3 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
            ABAC predicates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <h4 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
                Before
              </h4>
              <PredicateDiffList
                entries={predicateEntries.filter((e) => e.state !== 'added')}
                emptyLabel="No ABAC predicates attached."
              />
            </Card>
            <Card>
              <h4 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
                After
              </h4>
              <PredicateDiffList
                entries={predicateEntries.filter((e) => e.state !== 'removed')}
                emptyLabel="No ABAC predicates attached."
              />
            </Card>
          </div>
        </section>
      )}
    </div>
  );
}

export default EffectivePermissionsDiff;
